import View from '@play-co/timestep-core/lib/ui/View';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import { Feature } from 'src/replicant/analytics';
import { ImageView } from '@play-co/timestep-core/ui';
import PopupBasicLazy from '../../components/popups/PopupBasicLazy';
import { AssetGroup } from '@play-co/timestep-core';
import { PetSpeciesID } from 'src/replicant/pet';
import ShineFx from '../../minigame/match2-timestep/components/ShineFx';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import SparkleFx from '../../minigame/match2-timestep/team/components/SparkleFx';
import { PetEntity } from '../../logic/EntityManager';
import { assertNever } from 'src/replicant/utils';
import sounds from 'src/lib/sounds';
import { RevivalPopupInitArgs } from './PopupRevivalTall';

export default class PopupRevivalSimple extends PopupBasicLazy {
  constructor(opts: { superview: View; close: () => void }) {
    const { width } = uiConfig.popups.buruburu;

    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width,
      height: 641,
      darkerBg: false,
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.REVIVE,
      },
    });

    this.buttonClose.removeFromSuperview();

    new ButtonScaleViewWithText({
      superview: this.box,
      ...uiConfig.buttons.secondary,
      width: 238,
      height: 87,
      x: width * 0.5,
      y: 565,
      fontSize: 40,
      font: 'Body',
      centerOnOrigin: true,
      localeText: () => i18n('basic.ok'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close();
      },
      tracking: {
        feature: Feature.VISIT._,
        $subFeature: Feature.VISIT.REVIVE_OK,
      },
    });
  }

  /**
   * Preload assets before showing the popup
   */
  public getAssetsGroup() {
    return new AssetGroup(
      AssetGroup.constructURLs(['assets/ui/visit/revive/revival_intro.png']),
    );
  }

  /**
   * On popup show
   */
  public init(args: RevivalPopupInitArgs) {
    super.init({});

    switch (args.mode) {
      case 'intro':
        this.createIntroContent();
        break;
      case 'hungry':
        this.createHungryContent(args);
        break;
      case 'revived':
        this.createRevivedContent(args);
        break;
      default:
        assertNever(args);
    }
  }

  private createIntroContent() {
    this.title.setText(() => i18n('friendsVisit.revive.intro.title'));

    new ImageView({
      superview: this.box,
      image: 'assets/ui/visit/revive/revival_intro.png',
      centerOnOrigin: true,
      width: 459,
      height: 240,
      x: this.box.style.width * 0.5,
      y: 225,
    });

    this.message.updateOpts({
      localeText: () => i18n('friendsVisit.revive.intro.message'),
      width: 471,
      height: 128,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      wordWrap: true,
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: 430,
    });
  }

  private createHungryContent({ species }: { species: PetSpeciesID }) {
    this.title.setText(() => i18n('friendsVisit.revive.hungry.title'));

    const pet = new PetEntity('revivalPet', species);
    pet.updateOpts({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 415,
      scale: 0.7,
      anchorX: 0.5 * pet.style.width,
      anchorY: 0.5 * pet.style.height,
    });
    pet.statusClip.updateOpts({
      y: 40,
    });

    const petAnimations = petAnimationNames[species];
    pet.setAnimation(petAnimations.hungry.idle);
    pet.setStatusAnimation('hungry');

    this.message.updateOpts({
      localeText: () => i18n('friendsVisit.revive.hungry.message'),
      width: 471,
      height: 128,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      wordWrap: true,
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: 460,
    });
  }

  private createRevivedContent({
    species,
    friendName,
  }: {
    species: PetSpeciesID;
    friendName?: string;
  }) {
    this.title.setText(() => i18n('friendsVisit.revive.revived.title'));

    const shine = new ShineFx({
      superview: this.box,
    });
    shine.show();

    shine.updateOpts({
      x: this.box.style.width / 2,
      y: 265,
      centerOnOrigin: true,
    });

    const pet = new PetEntity('revivalPet', species);
    pet.updateOpts({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 355,
      anchorX: 0.5 * pet.style.width,
      anchorY: 0.5 * pet.style.height,
    });

    new SparkleFx({
      superview: this.box,
      width: 400,
      height: 400,
      x: shine.style.x,
      y: shine.style.y,
      amount: 5,
      scale: 1.5,
      shineDuration: 3,
    });

    const petAnimations = petAnimationNames[species];
    pet.setAnimation(petAnimations.play);

    const message = friendName
      ? 'friendsVisit.revive.revived.message'
      : 'friendsVisit.revive.revived.messageGeneric';

    this.message.updateOpts({
      localeText: () => i18n(message, { friendName }),
      width: 471,
      height: 128,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      wordWrap: true,
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: 460,
      zIndex: 10,
    });
  }
}
